import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import useAPI from "services/Api";
import Input from "input/Input";
import Page from "pages/Page";
import { useNotificationManager } from "notifications/NotificationManager";
import { useParams } from "react-router";

const SetPassword = ({ componentName = "[SetPassword]" }) => {
    const [clear, setClear] = useState(false);
    const [password1, setPassword1] = useState<string | null>(null);
    const [password2, setPassword2] = useState<string | null>(null);

    const API = useAPI();
    const notificationManager = useNotificationManager();
    const location = useLocation();
    const navigate = useNavigate();
    const paramsFromPath = useParams();

    // Read uid/token from URL path OR query string. Path parameters take precedence.
    const { uid, token } = useMemo(() => {
        const paramsFromQuery = new URLSearchParams(location.search);
        return {
            uid: paramsFromPath.uid ?? paramsFromQuery.get("uid") ?? undefined,
            token: paramsFromPath.token ?? paramsFromQuery.get("token") ?? undefined
        };
    }, [location.search, paramsFromPath]);

    useEffect(() => {
        if (clear) setClear(false);
    }, [clear]);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        notificationManager.clearAllNotifications();
        setClear(true);

        if (!uid || !token) {
            notificationManager.addError("Invalid or expired reset link.");
            return;
        }
        if (!password1 || !password2) {
            notificationManager.addError("Password must be filled in");
            return;
        }
        if (password1 !== password2) {
            notificationManager.addError("The passwords you entered do not match, please try again");
            return;
        }
        if (password1.length < 8 || password2.length < 8) {
            notificationManager.addError("Password must be at least 8 characters long");
            return;
        }

        const payload = JSON.stringify({
            uid,           // base36 uid (allauth)
            token,
            new_password1: password1,
            new_password2: password2
        });

        await API.api_Post(
            componentName,
            `/password/reset/confirm/`,
            payload,
            notificationManager,
            (response) => {
                if (response.status === 200) {
                    // Go to login and show a success banner via state
                    navigate("/login", {
                        replace: true,
                        state: { passwordResetSuccess: true }
                    });
                }
                else {
                    const message
                        = response?.data?.errors?.[0]?.message
                        || response?.data?.detail
                        || "Unable to reset password.";
                    throw new Error(message);
                }
            },
            (error) => {
                const message
                    = error?.response?.data?.errors?.[0]?.message
                    || error?.response?.data?.detail
                    || error?.message
                    || "Unable to reset password.";
                notificationManager.addError(message);
            }
        );
    };

    const hasParams = Boolean(uid && token);

    return (
        <Page componentName={componentName} internal={false}>
            {
                hasParams
                    ? (
                        <div className="h-screen flex items-center justify-center pb-12 ">
                            <div className="w-96 h-96 flex-col justify-start items-center gap-8 inline-flex">
                                <div className="self-stretch h-28 flex-col justify-start items-center gap-6 flex">
                                    <div className="w-14 h-14 p-3.5 rounded-xl shadow border border-gray-50 justify-center items-center inline-flex">
                                        <img src="/icons/lock-01.svg" className="w-7 h-7" alt="icon" />
                                    </div>
                                    <div className="self-stretch h-9 flex-col justify-start items-start gap-3 flex">
                                        <div className="self-stretch text-center text-gray-900 text-3xl font-semibold leading-9">
                                            Set a password
                                        </div>
                                    </div>
                                </div>
                                <form className="w-full max-w-sm" onSubmit={handleSubmit}>
                                    <div className="self-stretch h-56 rounded-xl flex-col justify-start items-center gap-6 flex">
                                        <div className="self-stretch h-40 flex-col justify-start items-start gap-5 flex">
                                            <div className="self-stretch h-16 flex-col justify-start items-start gap-1.5 flex">
                                                <div className="text-gray-700 text-sm font-medium leading-tight">Password</div>
                                                <Input
                                                    className={"self-stretch px-3.5 py-2.5 bg-white rounded-lg shadow border border-gray-300 text-gray-500 text-base"}
                                                    name="Password"
                                                    noLabel
                                                    required
                                                    type="password"
                                                    validations={[{ type: "minLength", value: 8 }]}
                                                    value={password1 ?? ""}
                                                    onChange={setPassword1}
                                                />
                                            </div>
                                            <div className="self-stretch h-16 flex-col justify-start items-start gap-1.5 flex">
                                                <div className="text-gray-700 text-sm font-medium leading-tight">Confirm password</div>
                                                <Input
                                                    className={"self-stretch px-3.5 py-2.5 bg-white rounded-lg shadow border border-gray-300 text-gray-500 text-base"}
                                                    name="Confirm Password"
                                                    noLabel
                                                    required
                                                    type="password"
                                                    validations={[{ type: "minLength", value: 8 }]}
                                                    value={password2 ?? ""}
                                                    onChange={setPassword2}
                                                />
                                            </div>
                                        </div>
                                        <button className="self-stretch px-4 py-2.5 bg-blue-500 hover:bg-blue-600 rounded-lg shadow border-2 border-blue-600 text-white text-base font-semibold leading-normal cursor-pointer">
                                            Set Password
                                        </button>
                                    </div>
                                </form>
                                <Link to="/login">
                                    <div className="justify-center items-center gap-1.5 inline-flex">
                                        <img src="/icons/arrow-left.svg" className="w-5 h-5" alt="icon" />
                                        <div className="text-gray-600 text-sm font-semibold leading-tight">Go to login</div>
                                    </div>
                                </Link>
                            </div>
                        </div>
                    )
                    : (
                        <div className="h-screen flex items-start justify-center pb-12 ">
                            <div className="w-96 h-52 flex-col justify-start items-center gap-8 inline-flex">
                                <div className="login">
                                    <p>Invalid or expired reset link.</p>
                                    <Link to="/login">Click here</Link> to sign in.
                                </div>
                            </div>
                        </div>
                    )
            }
        </Page>
    );
};

export default SetPassword;